import { useEffect, useMemo, useState } from "react";
import { useSharedGutter } from "@/lib/width-group/use-shared-gutter";
import { type SharedGutter, type WidthGroupRegistry, widestGutter } from "@/lib/width-group/width-group-registry";
import { layoutTextMeasurer, type TextMeasurer } from "@/utils/text";
import type { AxisSpec, StreamEntry } from "../../ChartSpec";
import type { PreparedSeries } from "../prepare-series";
import { type AxisScale, computeAxisExtents, computeAxisScale, targetTickCountForHeight } from "./axis-layout";
import { UNIT_BADGE_HEADROOM_PX, X_AXIS_BAND_PX } from "./plot-geometry";
import { computeGutterRequirement, derivePlotLayout, type PlotLayout } from "./plot-layout";

type PanelPlotLayout = { layout: PlotLayout; axisScales: Map<string, AxisScale> };

type PanelPlotLayoutInput = {
    axes: AxisSpec[];
    streams: StreamEntry[];
    preparedSeries: PreparedSeries[];
    activeLeftAxisId: string | undefined;
    panelHeightPx: number;
    groupId: string;
    memberId: string;
    registry: WidthGroupRegistry;
    /** Injectable for tests; defaults to the shared cached canvas measurer. */
    measure?: TextMeasurer;
};

/**
 * Bump when web fonts finish loading so gutters remeasure after layoutTextMeasurer clears its
 * fallback-glyph cache (`createTextMeasurer` in `@/utils/text`). Clearing the cache alone does not
 * re-run React memos — this revision is the invalidation token.
 *
 * Effect registers after the first measure’s fonts.ready listener, so the cache clears before this
 * revision flips.
 */
function useDocumentFontsReadyRevision(): number {
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const fonts = typeof document === "undefined" ? undefined : document.fonts;
        if (!fonts?.ready) return undefined;

        let cancelled = false;
        fonts.ready.then(
            () => {
                if (!cancelled) setRevision((n) => n + 1);
            },
            () => {
                // Ignore — stale gutters beat crashing.
            },
        );
        return () => {
            cancelled = true;
        };
    }, []);

    return revision;
}

/**
 * Computes plot layout: tick scales → how much gutter this panel needs → shared stack gutter → layout for ECharts and chrome.
 *
 * Gutter need must come from this panel's data only, not the shared gutter (or panels would keep widening each other).
 *
 * Call echarts.setOption from useLayoutEffect in panel.tsx, not useEffect — otherwise gutters jump one frame when a sibling mounts.
 *
 * Don't mutate layout.gutters; sibling panels may share that object.
 *
 * Layout ignores panel width (gutter need is from label text + tick count). Widening the panel only
 * needs chart.resize() — not a new PlotLayout / setOption.
 */
export function usePanelPlotLayout({
    axes,
    streams,
    preparedSeries,
    activeLeftAxisId,
    panelHeightPx,
    groupId,
    memberId,
    registry,
    measure = layoutTextMeasurer,
}: PanelPlotLayoutInput): PanelPlotLayout {
    const fontsReadyRevision = useDocumentFontsReadyRevision();

    const extents = useMemo(() => computeAxisExtents(preparedSeries, streams, axes), [preparedSeries, streams, axes]);

    const availablePlotHeightPx = Math.max(panelHeightPx - X_AXIS_BAND_PX, 1);
    const targetCount = targetTickCountForHeight(availablePlotHeightPx);

    const axisScales = useMemo(
        () =>
            new Map<string, AxisScale>(
                axes.flatMap((axis) => {
                    const extent = extents.get(axis.id);
                    if (!extent) return [];
                    const scale = computeAxisScale({
                        extent,
                        plotHeightPx: availablePlotHeightPx,
                        headroomPx: UNIT_BADGE_HEADROOM_PX,
                        targetCount,
                    });
                    return [[axis.id, scale] as const];
                }),
            ),
        [axes, extents, availablePlotHeightPx, targetCount],
    );

    const requirement = useMemo(
        () =>
            computeGutterRequirement({
                axes,
                activeLeftAxisId,
                scalesByAxis: axisScales,
                measure,
            }),
        // fontsReadyRevision forces remeasure after webfonts load (badge uses Roboto).
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional cache-bust dep
        [axes, activeLeftAxisId, axisScales, measure, fontsReadyRevision],
    );

    const sharedGutter = useSharedGutter(registry, groupId, memberId, requirement);

    // On first render we haven't registered our width yet, so the shared gutter may still be 0 and
    // labels would clip off the left edge. Use whichever is wider: the shared gutter or our own
    // measured need. Once every panel has registered, the shared gutter is already the max.
    const effectiveSharedGutter = useMemo<SharedGutter>(() => {
        const own = widestGutter([requirement]);
        if (sharedGutter.left >= own.left && sharedGutter.right >= own.right) return sharedGutter;
        return {
            left: Math.max(sharedGutter.left, own.left),
            right: Math.max(sharedGutter.right, own.right),
        };
    }, [sharedGutter, requirement]);

    const layout = useMemo(() => derivePlotLayout({ sharedGutter: effectiveSharedGutter }), [effectiveSharedGutter]);

    return useMemo(() => ({ layout, axisScales }), [layout, axisScales]);
}
